/**
 * assetStore — virtual /assets folder for the sandbox session.
 *
 * Assets live here as base64 (the "virtual disk"), mirrored to localStorage so
 * they survive reloads. The AssetManager reads them at runtime via a browser
 * AssetSource. Mirrors designStore's event-emitting + useSyncExternalStore shape.
 */
import { useSyncExternalStore } from 'react';
import {
  validateAssetFile,
  suggestAssetFolder,
  type AssetMimeType,
} from '@gotti/sandbox-shared';
import type { StoredAsset } from './types';
import { fetchAssetContentBytes } from './assetDecode';

const STORAGE_KEY = 'gotti.sandbox.assets.v1';

interface AssetState {
  assets: Map<string, StoredAsset>; // path -> asset
  version: number;
}

let state: AssetState = { assets: new Map(), version: 0 };
const listeners = new Set<() => void>();
function notify() { for (const fn of listeners) fn(); }

// ── persistence ──────────────────────────────────────────────────────
function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...state.assets.values()]));
  } catch { /* private mode — session-only is fine */ }
}
function load(): Map<string, StoredAsset> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const arr = JSON.parse(raw) as StoredAsset[];
    return new Map(arr.map((a) => [a.path, a]));
  } catch { return new Map(); }
}
state = { assets: load(), version: 1 };

// ── helpers ──────────────────────────────────────────────────────────
function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ── public API ───────────────────────────────────────────────────────
export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export function listAssets(): StoredAsset[] {
  return [...state.assets.values()].sort((a, b) => a.path.localeCompare(b.path));
}
export function getAsset(path: string): StoredAsset | undefined {
  return state.assets.get(path);
}

/** Add an asset from raw bytes. Throws on validation failure. */
export async function addAssetBytes(name: string, mimeType: AssetMimeType, bytes: Uint8Array): Promise<StoredAsset> {
  const v = validateAssetFile(name, mimeType, bytes.length);
  if (!v.valid) throw new Error(v.error ?? 'Invalid asset');
  const folder = suggestAssetFolder(mimeType);
  const path = folder ? `${folder}/${name}` : name;
  const asset: StoredAsset = {
    path, name, folder, mimeType,
    size: bytes.length,
    hash: await sha256Hex(bytes),
    base64: bytesToBase64(bytes),
    preload: true,
  };
  const next = new Map(state.assets);
  next.set(path, asset);
  state = { assets: next, version: state.version + 1 };
  persist(); notify();
  return asset;
}

/** Add an asset from a browser File (UI upload path). */
export async function addAssetFile(file: File): Promise<StoredAsset> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  return addAssetBytes(file.name, file.type as AssetMimeType, bytes);
}

/**
 * Wire shape of one pipeline-materialized asset, as carried by the
 * `v2:asset:created` SSE frame (sandbox-ai-v2 `MaterializedAsset`, minus the
 * specId the store doesn't care about). Bytes arrive inline (`base64`, when
 * under the worker's inline cap — placeholder assets always are) or by
 * reference: a direct `url`, else the backend's content-addressed endpoint
 * keyed by `hash` (shared contract in `assetDecode.fetchAssetContentBytes`).
 */
export interface GeneratedAssetInput {
  path: string;
  name: string;
  folder?: string;
  mimeType: string;
  size: number;
  hash: string;
  base64?: string;
  url?: string;
}

/**
 * Fold a pipeline-generated asset into the session store.
 *
 * Same-path upsert = swap (Map.set), so a re-generated sprite replaces the
 * previous bytes exactly like a re-upload would. The store keeps raw base64
 * (its durable representation) — inline frames are stored as-is, reference
 * frames are fetched then encoded. Persists + notifies, which also triggers
 * the knowledge-graph re-index via its existing store subscription.
 *
 * NO validateAssetFile gate here: these bytes come from our own worker (which
 * already enforced the spec contract), and rejecting e.g. a mime type the
 * upload allow-list doesn't know would silently drop a generated asset.
 */
export async function upsertGeneratedAsset(asset: GeneratedAssetInput): Promise<StoredAsset> {
  let base64 = asset.base64;
  if (base64 === undefined) {
    base64 = bytesToBase64(await fetchAssetContentBytes(asset, 'upsertGeneratedAsset'));
  }
  // path ≡ `${folder}/${name}` on the wire; derive the bucket when omitted so
  // the AssetsPanel's folder grouping stays coherent.
  const folder = asset.folder ?? (asset.path.includes('/') ? asset.path.slice(0, asset.path.lastIndexOf('/')) : '');
  const stored: StoredAsset = {
    path: asset.path,
    name: asset.name,
    folder,
    mimeType: asset.mimeType as AssetMimeType,
    size: asset.size,
    hash: asset.hash,
    base64,
    origin: 'generated',
    preload: true,
  };
  const next = new Map(state.assets);
  next.set(stored.path, stored);
  state = { assets: next, version: state.version + 1 };
  persist(); notify();
  return stored;
}

export function removeAsset(path: string): void {
  if (!state.assets.has(path)) return;
  const next = new Map(state.assets);
  next.delete(path);
  state = { assets: next, version: state.version + 1 };
  persist(); notify();
}

/** Set whether an asset is preloaded before game systems start. */
export function setAssetPreload(path: string, on: boolean): void {
  const existing = state.assets.get(path);
  if (!existing) return;
  const next = new Map(state.assets);
  next.set(path, { ...existing, preload: on });
  state = { assets: next, version: state.version + 1 };
  persist(); notify();
}

/** Replace the entire store (import path). */
export function replaceAllAssets(assets: StoredAsset[]): void {
  state = { assets: new Map(assets.map((a) => [a.path, a])), version: state.version + 1 };
  persist(); notify();
}

export function clearAssets(): void {
  state = { assets: new Map(), version: state.version + 1 };
  persist(); notify();
}

export function useAssets(): StoredAsset[] {
  const version = useSyncExternalStore(subscribe, () => state.version, () => 0);
  // Reading `version` ensures React re-runs this hook when the store changes.
  void version;
  return listAssets();
}
